import { useEffect, useRef, useState } from "react";
import { CircleNotch, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { StepContainer } from "../components/StepContainer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { STEPS, findStepIndexBySlug } from "../config/steps";
import { toastError } from "@/lib/userMessages";
import { throwEdgeFnError } from "@/lib/edgeFnError";

interface Values {
  bio: string;
  unique_mechanism: string;
  differentiator: string;
  pains_solved: string;
  transformation_promise: string;
}

/** Minimum chars per field — lowered from 80 → 40 in Tier-5c onboarding redux
 *  so first-time users aren't blocked by long-form positioning when the step
 *  is now optional anyway. */
const MIN = 40;

const POSITIONING_EXAMPLES: ReadonlyArray<string> = [
  "Ex: 'Médica integrativa que ajuda mulheres 35+ com sintomas de TPM através de protocolo XYZ.'",
  "Ex: 'Coach de carreira para devs sêniores em transição para tech leads.'",
  "Ex: 'Nutricionista clínico para pacientes pós-bariátrica retomarem qualidade de vida.'",
];

const FIELDS: Array<{
  key: keyof Values;
  label: string;
  placeholder: string;
  hint: string;
}> = [
  {
    key: "bio",
    label: "Mini-bio",
    placeholder: "Quem você é em 1-2 frases para um post no Instagram.",
    hint: "Como você se apresentaria na bio do feed.",
  },
  {
    key: "unique_mechanism",
    label: "Mecanismo único",
    placeholder: "Sua metodologia ou abordagem própria que entrega resultado.",
    hint: "O 'como' que só você faz desse jeito.",
  },
  {
    key: "differentiator",
    label: "Diferencial",
    placeholder: "O que te coloca à frente dos concorrentes diretos.",
    hint: "Por que escolheriam você no lugar de outro profissional.",
  },
  {
    key: "pains_solved",
    label: "Dores que você resolve",
    placeholder: "Os problemas concretos que seus clientes vivem antes de te procurar.",
    hint: "Use a linguagem do cliente, não termos técnicos.",
  },
  {
    key: "transformation_promise",
    label: "Promessa de transformação",
    placeholder: "Qual é o resultado tangível que o cliente alcança ao trabalhar com você.",
    hint: "Antes → depois. Concreto, mensurável, honesto.",
  },
];

export interface PositioningStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function PositioningStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: PositioningStepProps) {
  const step = STEPS[findStepIndexBySlug("posicionamento")];
  const { mutate, mutateDebounced } = useUpdateBusinessContext();
  const { query } = useBusinessContext();

  const [values, setValues] = useState<Values>({
    bio: "",
    unique_mechanism: "",
    differentiator: "",
    pains_solved: "",
    transformation_promise: "",
  });
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    const row = query.data;
    if (!row) return;
    setValues({
      bio: row.bio ?? "",
      unique_mechanism: row.unique_mechanism ?? "",
      differentiator: row.differentiator ?? "",
      pains_solved: row.pains_solved ?? "",
      transformation_promise: row.transformation_promise ?? "",
    });
  }, [query.data]);

  useEffect(() => {
    const allOk = FIELDS.every((f) => (values[f.key] ?? "").trim().length >= MIN);
    onValidityChange(allOk);
    if (onMissingChange) {
      const missing = FIELDS.filter(
        (f) => (values[f.key] ?? "").trim().length < MIN,
      ).map((f) => f.label.toLowerCase());
      onMissingChange(missing);
    }
  }, [values, onValidityChange, onMissingChange]);

  useEffect(() => {
    onAttemptContinue(async () => {
      const v = valuesRef.current;
      const allOk = FIELDS.every((f) => (v[f.key] ?? "").trim().length >= MIN);
      if (!allOk) return false;
      await mutate(v);
      return true;
    });
  }, [mutate, onAttemptContinue]);

  const handleChange = (key: keyof Values, val: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: val };
      mutateDebounced({ [key]: val });
      return next;
    });
  };

  const { session } = useSession();
  const [aiLoading, setAiLoading] = useState(false);

  const handleAiAssist = async () => {
    if (aiLoading) return;
    if (!session) {
      toast.error("Sessão não pronta. Tente novamente em alguns segundos.");
      return;
    }
    setAiLoading(true);
    try {
      const token = await session.getToken({ template: "supabase" });
      if (!token) throw new Error("Token de sessão indisponível");

      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
      if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL não configurado");

      const row = query.data;
      const resp = await fetch(`${supabaseUrl.replace(/\/$/, "")}/functions/v1/positioning-suggest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current: valuesRef.current,
          identity: {
            honorific_prefix: row?.honorific_prefix ?? null,
            niche: row?.niche ?? null,
            business_name: row?.display_name ?? null,
          },
        }),
      });

      if (!resp.ok) {
        await throwEdgeFnError(resp, `HTTP ${resp.status}`);
      }
      const json = (await resp.json()) as { suggestions: Values };
      const next = json.suggestions;

      setValues(next);
      mutate(next);
      toast.success("Rascunho preenchido pela IA. Revise e ajuste o que quiser.");
    } catch (err) {
      toastError(err, "Não foi possível gerar o rascunho. Tente novamente.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <StepContainer
      icon={step.icon}
      headline={step.headline}
      sub={step.sub}
      importanceNote="Quanto mais específico, mais a IA imita seu posicionamento."
    >
      <div className="flex flex-col gap-5">
        {/* Examples + AI assist (TIER-5c onboarding redux). The IA button is
         *  intentionally disabled until a server endpoint exists.
         *  TIER-N FOLLOWUP: wire AI assist via /functions/positioning-suggest */}
        <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Exemplos de posicionamento
          </p>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {POSITIONING_EXAMPLES.map((ex) => (
              <li key={ex} className="leading-snug">{ex}</li>
            ))}
          </ul>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAiAssist}
            disabled={aiLoading}
            className="mt-1 gap-1.5"
          >
            {aiLoading ? (
              <CircleNotch weight="bold" className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkle weight="fill" className="size-3.5" aria-hidden />
            )}
            {aiLoading ? "Gerando rascunho…" : "Sugerir com IA"}
          </Button>
        </div>
        {FIELDS.map((f) => {
          const val = values[f.key] ?? "";
          const len = val.trim().length;
          const ok = len >= MIN;
          return (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`pos-${f.key}`}>{f.label}</Label>
              <Textarea
                id={`pos-${f.key}`}
                value={val}
                onChange={(e) => handleChange(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={3}
                className="resize-none"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{f.hint}</span>
                <span className={ok ? "text-emerald-500" : "text-muted-foreground"}>
                  {len}/{MIN}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </StepContainer>
  );
}
